/**
 * Strongly-typed map of form field validation error messages.
 * Keys are `formControlName` values; nested keys are Angular validator error keys.
 *
 * Apps can extend or override individual entries via the `customErrors`
 * parameter on `getFormControlError()` or the `FormFieldErrorComponent`.
 */

export type ValidationErrorKey =
  | 'required'
  | 'pattern'
  | 'mismatch'
  | 'invalid'
  | 'maxlength'
  | 'minlength'
  | 'min'
  | 'max'
  | 'email';

export type FieldErrorMessages = Partial<Record<ValidationErrorKey, string>>;

export const FieldErrors: Record<string, FieldErrorMessages> = {
  first_name: {
    required: 'Please enter first name.',
    invalid: 'Please enter a valid first name.',
  },
  last_name: {
    required: 'Please enter last name.',
    invalid: 'Please enter a valid last name.',
  },
  address: {
    required: 'Please enter address.',
    invalid: 'Please select an address from suggestions.',
  },
  apartment: {
    required: 'Please enter apartment.',
  },
  city: {
    required: 'Please enter city.',
  },
  state: {
    required: 'Please enter state.',
  },
  delivery_note: {
    maxlength: 'Please enter less than 120 characters.',
  },
  zipCode: {
    required: 'Please enter zipcode.',
    pattern: 'Please enter a valid zipcode.',
  },
  recipientZipCode: {
    required: 'Please enter recipient zipcode.',
    pattern: 'Please enter a valid recipient zipcode.',
  },
  phone: {
    required: 'Please enter phone number.',
    pattern: 'Please enter 10 digit numbers only.',
  },
  ageRange: {
    required: 'Please select your age range.',
  },
  email: {
    required: 'Please enter email.',
    pattern: 'Please enter a valid email.',
  },
  confirm_email: {
    required: 'Please confirm your email.',
    mismatch: 'Email addresses do not match.',
  },
  password: {
    required: 'Please enter password.',
  },
  confirm_password: {
    required: 'Please confirm your password.',
    mismatch: 'Passwords do not match.',
  },
  new_password: {
    required: 'Please enter new password.',
    invalid: 'Current password and new password cannot be the same.',
  },
  confirm_new_password: {
    required: 'Please re-enter new password.',
    mismatch: 'Confirm password does not match new password.',
  },
  survey_text: {
    required: 'Please enter a description.',
  },
  recommendationRating: {
    required: 'Please select your recommendation rating.',
  },
  sms_consent: {
    required: 'Please agree to the terms.',
  },
  consent: {
    required: 'Please check the checkbox.',
  },
  feedbackMessage: {
    required: 'Please give your feedback.',
  },
  personal_message: {
    required: 'Please enter personal message.',
    maxlength: 'Please enter less than 30 characters.',
  },
  nameOnCard: {
    required: 'Please enter name.',
  },
  promoRadio: {
    required: 'Please select code type.',
  },
  promoCode: {
    required: 'Please enter promo code.',
  },
  couponCode: {
    required: 'Please enter coupon code.',
  },
  sender_first_name: {
    required: 'Please enter sender first name.',
  },
  sender_last_name: {
    required: 'Please enter sender last name.',
  },
  customAmount: {
    required: 'Please enter an amount.',
    min: 'Please enter an amount of at least $25.',
    max: 'Please enter an amount no more than $500.',
  },
  no_of_persons: {
    required: 'Please select number of persons.',
  },
  no_of_recipes: {
    required: 'Please select number of recipes.',
  },
  plan_type: {
    required: 'Please select your plan.',
  },
};

/**
 * Common regex patterns for form field validation.
 * Use with Angular `Validators.pattern()`.
 */
export const patterns = {
  email:
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  zipCode: /^[0-9]{5}$/,
  phone: /^[0-9]{10}$/,
} as const satisfies Record<string, RegExp>;
